/**
 * TSGo Design System Tokens
 *
 * Single source of truth for brand colors, typography, spacing, and motion.
 * These tokens mirror the CSS variables defined in `src/index.css` and are
 * exposed to TypeScript for programmatic access (e.g., charts, inline styles).
 */

export const brand = {
    name: "TSGo",
    tagline: "Everything You Need. Delivered Fast.",
} as const;

export const colors = {
    primary: "#00C853",
    secondary: "#FFC107",
    background: "#FFFFFF",
    foreground: "#111827",
    success: "#16A34A",
    error: "#DC2626",
    muted: "#6B7280",
    border: "#E5E7EB",
} as const;

export const typography = {
    fontFamily: "Poppins, ui-sans-serif, system-ui, sans-serif",
    weights: {
        light: 300,
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800,
    },
    sizes: {
        xs: "0.75rem",
        sm: "0.875rem",
        base: "1rem",
        lg: "1.125rem",
        xl: "1.25rem",
        "2xl": "1.5rem",
        "3xl": "1.875rem",
        "4xl": "2.25rem",
        "5xl": "3rem",
    },
} as const;

export const radii = {
    sm: "0.5rem",
    md: "0.625rem",
    lg: "0.75rem",
    xl: "1rem",
    pill: "999px",
} as const;

export const shadows = {
    soft: "0 4px 20px -6px rgba(17, 24, 39, 0.08)",
    elevated: "0 12px 32px -12px rgba(17, 24, 39, 0.16)",
    brand: "0 10px 24px -10px rgba(0, 200, 83, 0.45)",
} as const;

export const breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1200,
    "2xl": 1320,
} as const;

export const motion = {
    duration: {
        fast: 150,
        base: 250,
        slow: 400,
    },
    easing: {
        standard: "cubic-bezier(0.4, 0, 0.2, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
    },
} as const;

export type ThemeTokens = {
    brand: typeof brand;
    colors: typeof colors;
    typography: typeof typography;
    radii: typeof radii;
    shadows: typeof shadows;
    breakpoints: typeof breakpoints;
    motion: typeof motion;
};

export const tokens: ThemeTokens = {
    brand,
    colors,
    typography,
    radii,
    shadows,
    breakpoints,
    motion,
};
